import type { ResultStateName } from '@/api/runEvidence';
import type {
  CoverageRow,
  EvaluationWorklist,
  ExploratoryCounts,
  NotAssessedGroup,
  NotAssessedKind,
  WorklistCounts,
  WorklistItem,
  WorklistRunningJob,
} from '@/api/evaluationWorklist';

// ─────────────────────────────────────────────────────────────────────────────
// The Overview's words (Scenario Intelligence screen 01). Pure: every count and state is the server's
// (EvaluationWorklistRules); this only groups the rows the server ordered and says them in sentences. Nothing here
// invents a state, a score or a percentage, and coverage is said per category, never per requirement.
// ─────────────────────────────────────────────────────────────────────────────

/** The server's order (EvaluationWorklistRules.StateOrder). */
export const STATE_ORDER: ResultStateName[] = ['NeedsAttention', 'AwaitingReview', 'EvidenceIncomplete', 'NotAssessed', 'Passed'];

export const STATE_HINT: Record<ResultStateName, string> = {
  NeedsAttention: 'a check failed on the result that counts',
  AwaitingReview: 'every automatic check passed; an obligation a reviewer judges waits for one',
  EvidenceIncomplete: 'not a pass: something a check needed was not captured',
  NotAssessed: 'no result counts for the current version',
  Passed: 'every check that applied passed',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The library's rows by state, in the server's order, and the drafts apart. A draft is never in a state group. */
export function groupByState(items: WorklistItem[]): Record<ResultStateName, WorklistItem[]> & { Exploratory: WorklistItem[] } {
  const groups = { NeedsAttention: [], AwaitingReview: [], EvidenceIncomplete: [], NotAssessed: [], Passed: [], Exploratory: [] } as Record<ResultStateName, WorklistItem[]> & {
    Exploratory: WorklistItem[];
  };
  for (const item of items) {
    if (item.exploratory) groups.Exploratory.push(item);
    else groups[item.state].push(item);
  }
  return groups;
}

/** True when the five counts add up to the library and each matches its group of rows — what the screen promises. */
export function countsAgree(w: Pick<EvaluationWorklist, 'counts' | 'items'>): boolean {
  const c = w.counts;
  const g = groupByState(w.items);
  return (
    STATE_ORDER.reduce((sum, state) => sum + countOf(c, state), 0) === c.library &&
    g.NeedsAttention.length === c.needsAttention &&
    g.AwaitingReview.length === (c.awaitingReview ?? 0) &&
    g.EvidenceIncomplete.length === c.evidenceIncomplete &&
    g.NotAssessed.length === c.notAssessed &&
    g.Passed.length === c.passed
  );
}

export const countOf = (c: WorklistCounts, state: ResultStateName): number =>
  ({
    NeedsAttention: c.needsAttention,
    AwaitingReview: c.awaitingReview ?? 0,
    EvidenceIncomplete: c.evidenceIncomplete,
    NotAssessed: c.notAssessed,
    Passed: c.passed,
  })[state];

/** "The five states count the 39 library scenarios on their current version, each in exactly one state (3 + 0 + 2 + 8 + 26 = 39)." */
export function countRule(c: WorklistCounts): string {
  if (c.library === 0) return 'This agent has no library scenario yet, so there is nothing to count.';
  return (
    `The five states count the ${plural(c.library, 'library scenario', 'library scenarios')} on ${c.library === 1 ? 'its' : 'their'} current version, ` +
    `each in exactly one state (${STATE_ORDER.map((state) => countOf(c, state)).join(' + ')} = ${c.library}).`
  );
}

/** Exploratory is a filter, not a state: what the drafts' results said, never counted toward release. */
export function exploratorySentence(e: ExploratoryCounts): string {
  if (e.drafts === 0) return 'Exploratory is a filter, not a state: this agent has no draft.';
  const ran = e.drafts - e.notRun;
  const parts: string[] = [];
  if (e.needsAttention) parts.push(`${e.needsAttention} failed`);
  if (e.awaitingReview) parts.push(`${e.awaitingReview} awaiting a reviewer`);
  if (e.evidenceIncomplete) parts.push(`${e.evidenceIncomplete} with evidence incomplete`);
  if (e.notAssessed) parts.push(`${e.notAssessed} did not complete`);
  if (e.passed) parts.push(`${e.passed} passed`);
  if (e.notRun) parts.push(`${e.notRun} not run on ${e.notRun === 1 ? 'its' : 'their'} current version`);
  return (
    `${plural(e.drafts, 'draft', 'drafts')}: ${parts.join(', ')}. ` +
    (ran > 0 ? 'Their results never count toward release; approving a draft does not promote them, a new run after approval does.' : 'A draft that runs is Exploratory and never counts toward release.')
  );
}

const KIND_WORDS: Record<NotAssessedKind, (n: number) => string> = {
  EditedSinceRun: (n) => `${n} ${n === 1 ? 'was' : 'were'} edited since ${n === 1 ? 'its' : 'their'} last run (the old result tested a different version)`,
  NeverRun: (n) => `${n} ${n === 1 ? 'has' : 'have'} never run`,
  EarlierAgentVersion: (n) => `${n} last ran on an earlier version of the agent`,
  TooOld: (n) => `${n} last ran on this version more than 30 days ago`,
  VersionNotRecorded: (n) => `${n} last ran before runs recorded which agent version they tested`,
  AgentVersionUnreadable: (n) => `${n} cannot be matched because the agent's current version could not be read`,
  DidNotComplete: (n) => `${n} did not complete (an execution error, not the agent's failure)`,
  NotRead: (n) => `${n} had a result that could not be read`,
  Quarantined: (n) => `${n} ${n === 1 ? 'is' : 'are'} quarantined by ${n === 1 ? 'its' : 'their'} owner`,
};

/** "5 were edited since their last run (…) and 3 have never run." — the server's groups, in its order. */
export function notAssessedSentence(groups: NotAssessedGroup[]): string {
  const parts = groups.filter((g) => g.count > 0).map((g) => KIND_WORDS[g.kind]?.(g.count) ?? `${g.count} ${g.kind}`);
  if (parts.length === 0) return 'Every library scenario has a result that counts for its current version.';
  const joined = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return joined.charAt(0).toUpperCase() + joined.slice(1) + '.';
}

/** The Not assessed scenarios a run would give a result: everything but a quarantined one. */
export const runnable = (items: WorklistItem[]): WorklistItem[] => items.filter((i) => !i.exploratory && i.state === 'NotAssessed' && i.notAssessedKind !== 'Quarantined');

/** "5 of 6 passed" with its denominator; a category with nothing assessed says so, and one with no scenario is uncovered. */
export function coverageResult(row: CoverageRow): { headline: string; detail: string | null } {
  if (row.scenarios === 0) return { headline: 'Uncovered', detail: null };
  const awaiting = row.awaitingReview ?? 0;
  const assessed = row.needsAttention + awaiting + row.evidenceIncomplete + row.passed;
  // "5 of 6 passed" when every one has a result that counts; otherwise the denominator says it is the assessed ones.
  const headline =
    assessed === 0 ? `0 of ${row.scenarios} assessed` : row.notAssessed === 0 ? `${row.passed} of ${assessed} passed` : `${row.passed} of ${assessed} assessed passed`;
  const extra: string[] = [];
  if (row.needsAttention) extra.push(`${row.needsAttention} need${row.needsAttention === 1 ? 's' : ''} attention`);
  if (awaiting) extra.push(`${awaiting} awaiting review`);
  if (row.evidenceIncomplete) extra.push(`${row.evidenceIncomplete} evidence incomplete`);
  if (row.notAssessed && assessed > 0) extra.push(`${row.notAssessed} not assessed`);
  return { headline, detail: extra.length ? extra.join(' · ') : null };
}

/** The coverage footer: what was counted, with its denominators. */
export function coverageSummary(w: Pick<EvaluationWorklist, 'coverage' | 'counts'>): string {
  const c = w.coverage;
  if (c.categories === 0) return 'No category list is recorded for this agent’s scenarios, so coverage cannot be counted by category.';
  return (
    `${plural(c.categories, 'category', 'categories')}${c.domain ? ` in the ${c.domain} list` : ''}: ` +
    `${c.covered} covered, ${c.uncovered} uncovered. ${plural(w.counts.library, 'library scenario', 'library scenarios')} counted once each.`
  );
}

/** "Run #25 is running: 14 of 39 scenarios finished" — or what the request is waiting for. */
export function runningTitle(job: WorklistRunningJob): string {
  const name = job.runId != null ? `Run #${job.runId}` : `Run request #${job.jobId}`;
  const progress = job.expectedTrials != null ? `${job.completedTrials} of ${plural(job.expectedTrials, 'scenario', 'scenarios')} finished` : `${plural(job.completedTrials, 'scenario', 'scenarios')} finished so far`;
  switch (job.state) {
    case 'Running':
      return `${name} is running: ${progress}`;
    case 'CancelRequested':
      return `${name} is stopping: ${progress}`;
    case 'DispatchPending':
    case 'Queued':
      return `${name} is waiting to start`;
    default:
      return `${name}: ${job.state}`;
  }
}

/** What a row's one next action says. */
export function actionLabel(item: WorklistItem): string | null {
  switch (item.nextAction) {
    case 'OpenFinding':
      return item.state === 'NeedsAttention' ? 'Open finding' : 'Open result';
    case 'Run':
      return 'Run…';
    default:
      return null;
  }
}

/** The meta line under a row: which run and turn, and the cause only when a PERSON recorded one. */
export function itemMeta(item: WorklistItem): string {
  const parts: string[] = [];
  if (item.runId != null && item.resultCounts) parts.push(`Run #${item.runId}`);
  else if (item.runId != null) parts.push(`Last result: run #${item.runId}, not counted`);
  if (item.turn != null) parts.push(`turn ${item.turn + 1}`);
  if (item.state === 'NeedsAttention') parts.push(item.findingCause ? `Cause recorded by a person: ${item.findingCause.toLowerCase()}` : 'Cause: not classified yet');
  if (item.isSafety) parts.push('Safety · required');
  return parts.join(' · ');
}

/** The workspace's URL for a tab, carrying the agent. */
export function evaluationPath(tab: '' | 'coverage' | 'scenarios' | 'runs' | 'compare' | 'reviews', agentId?: number | null): string {
  const base = tab ? `/evaluation/${tab}` : '/evaluation';
  return agentId != null ? `${base}?agent=${agentId}` : base;
}
